import React, { useState, useEffect, useCallback } from 'react';
import { Download, X, Smartphone, Share, PlusSquare, ChevronRight } from 'lucide-react';

// Detect iOS Safari
const isIOSSafari = () => {
  const ua = window.navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const webkit = /WebKit/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|mercury/.test(ua);
  return iOS && webkit && isSafari;
};

// Check if running as installed PWA
const isStandalone = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    document.referrer.includes('android-app://');
};

// iOS Install Instructions Modal
const IOSInstallModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div 
        className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1A2B3C] to-[#2A3B4C] p-5 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Install Dotcos App</h3>
                <p className="text-sm text-slate-300">Add to your home screen</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors p-1"
              data-testid="ios-install-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Instructions */}
        <div className="p-5 space-y-4">
          <p className="text-slate-600 text-sm">
            Install the Dotcos app for faster shopping and easy access from your home screen.
          </p>

          {/* Step 1 */}
          <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
            <div className="flex-shrink-0 w-8 h-8 bg-[#007AFF] text-white rounded-full flex items-center justify-center font-bold text-sm">
              1
            </div>
            <div className="flex-1">
              <p className="font-medium text-slate-800 mb-1">
                Tap the Share button
              </p>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Share className="w-5 h-5 text-[#007AFF]" />
                <span>at the bottom of Safari</span>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
            <div className="flex-shrink-0 w-8 h-8 bg-[#007AFF] text-white rounded-full flex items-center justify-center font-bold text-sm">
              2
            </div>
            <div className="flex-1">
              <p className="font-medium text-slate-800 mb-1">
                Scroll down and tap
              </p>
              <div className="flex items-center gap-2 text-sm">
                <div className="flex items-center gap-2 bg-white border border-slate-200 px-3 py-1.5 rounded-lg">
                  <PlusSquare className="w-4 h-4 text-slate-600" />
                  <span className="text-slate-700 font-medium">Add to Home Screen</span>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
            <div className="flex-shrink-0 w-8 h-8 bg-[#007AFF] text-white rounded-full flex items-center justify-center font-bold text-sm">
              3
            </div>
            <div className="flex-1">
              <p className="font-medium text-slate-800 mb-1">
                Tap "Add" to confirm
              </p>
              <p className="text-sm text-slate-500">
                The app will appear on your home screen
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5">
          <button
            onClick={onClose}
            className="w-full bg-[#1A2B3C] hover:bg-[#2A3B4C] text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
            data-testid="ios-install-got-it"
          >
            Got it!
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (isStandalone()) {
      setIsInstalled(true);
      return;
    }

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa_install_dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      // Show again after 7 days
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;
    }

    // Detect iOS
    const iOS = isIOSSafari();
    setIsIOS(iOS);

    if (iOS) {
      // For iOS, show banner after a delay
      setTimeout(() => setShowBanner(true), 3000);
    } else {
      // For other browsers, listen for beforeinstallprompt
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        // Delay to not compete with cookie consent
        setTimeout(() => setShowBanner(true), 3000);
      };
      window.addEventListener('beforeinstallprompt', handler);

      const installedHandler = () => {
        setIsInstalled(true);
        setShowBanner(false);
      };
      window.addEventListener('appinstalled', installedHandler);

      return () => {
        window.removeEventListener('beforeinstallprompt', handler);
        window.removeEventListener('appinstalled', installedHandler);
      };
    }
  }, []);

  const handleInstall = useCallback(async () => {
    if (isIOS) {
      // Show iOS installation modal
      setShowIOSModal(true);
      return;
    }

    if (!deferredPrompt) return;
    setInstalling(true);
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
    setShowBanner(false);
    setInstalling(false);
  }, [deferredPrompt, isIOS]);

  const handleDismiss = useCallback(() => {
    setShowBanner(false);
    localStorage.setItem('pwa_install_dismissed', Date.now().toString());
  }, []);

  const handleCloseIOSModal = useCallback(() => {
    setShowIOSModal(false);
    setShowBanner(false);
    localStorage.setItem('pwa_install_dismissed', Date.now().toString());
  }, []);

  if (isInstalled) return null;

  return (
    <>
      {/* iOS Install Modal */}
      {showIOSModal && <IOSInstallModal onClose={handleCloseIOSModal} />}

      {/* Install Banner */}
      {showBanner && !showIOSModal && (
        <div
          data-testid="pwa-install-banner"
          className="fixed bottom-20 left-3 right-3 sm:left-4 sm:right-4 md:left-auto md:right-6 md:bottom-24 md:w-[380px] z-[9999] animate-slide-up"
        >
          <div className="bg-[#1A2B3C] rounded-2xl shadow-2xl border border-[#D4AF37]/30 overflow-hidden">
            {/* Gold accent bar */}
            <div className="h-1 bg-gradient-to-r from-[#D4AF37] via-[#E8C860] to-[#D4AF37]" />

            <div className="p-4 flex items-start gap-3">
              {/* Icon */}
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-[#D4AF37]" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-sm leading-tight">
                  Get the Dotcos App
                </h3>
                <p className="text-slate-400 text-xs mt-0.5 leading-snug">
                  {isIOS 
                    ? 'Add to your home screen for the best experience'
                    : 'Install for faster shopping, quick reorders & offline browsing'
                  }
                </p>

                <div className="flex items-center gap-2 mt-3">
                  <button
                    data-testid="pwa-install-button"
                    onClick={handleInstall}
                    disabled={installing}
                    className="flex items-center gap-1.5 bg-[#D4AF37] hover:bg-[#E8C860] text-[#1A2B3C] font-semibold text-xs px-4 py-2 rounded-lg transition-colors"
                  >
                    {isIOS ? (
                      <>
                        <Share className="w-3.5 h-3.5" />
                        How to Install
                      </>
                    ) : (
                      <>
                        <Download className="w-3.5 h-3.5" />
                        {installing ? 'Installing...' : 'Install'}
                      </>
                    )}
                  </button>
                  <button
                    data-testid="pwa-install-dismiss"
                    onClick={handleDismiss}
                    className="text-slate-400 hover:text-white text-xs px-3 py-2 transition-colors"
                  >
                    Not now
                  </button>
                </div>
              </div>

              {/* Close button */}
              <button
                data-testid="pwa-install-close"
                onClick={handleDismiss}
                className="flex-shrink-0 text-slate-500 hover:text-white transition-colors p-0.5"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;
